import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import DataTableComponent from "../../common/DataTableComponent";
import { GetFirstAidCheckHistory } from "../../../services/FirstAidBoxService";
import { useFirstAidCheckHistory, useFirstAidReportFilters } from "../../../query/hooks/QueryHooksFirstAidBox";
import { CONDITION_LABELS, EVENT_TYPE_LABELS, OHS_BASE_PATH, REPORT_DATE_TIME_FORMAT } from "./ohsConstants";
import { ConditionBadge, MutedText } from "./Ohs.styles";
import { describeTagCheck, exportOhsReport, getItemStatuses, getPrefixedUser } from "./ohsUtils";
import ConditionBadges from "./ConditionBadges";
import OhsUserCell from "./OhsUserCell";

const EMPTY_FILTER = { arr: [], checkObj: {} };
// Search-style filters keep their ticked options in an array, not an object.
const EMPTY_SEARCH_FILTER = { arr: [], checkObj: [] };
const CHECK_EXPORT_COLUMNS = [
    { label: "Date", width: 12 },
    { label: "Time", width: 7 },
    { label: "Check Type", width: 15 },
    { label: "Checked By", width: 20 },
    { label: "Campus", width: 14 },
    { label: "Building", width: 22 },
    { label: "Area", width: 18 },
    { label: "Box", width: 16 },
    { label: "Tag Found", width: 12 },
    { label: "New Tag Fitted", width: 16 },
    { label: "Tag Check", width: 30 },
    { label: "Box Condition", width: 20 },
    { label: "Items Needing Attention", width: 12 },
];

/**
 * What happened to a check's tag, in words.
 * @param {Object} check Check History row.
 * @returns {string} e.g. "Intact - box not opened (TAG-1)" or "TAG-1 replaced with TAG-2"
 */
function describeTagChange(check) {
    if (check.is_tag_intact) {
        return `Intact - box not opened (${check.tag_serial_found})`;
    }
    return `${check.tag_serial_found || "No tag"} replaced with ${check.new_tag_serial}`;
}

/**
 * A list of condition keys as readable labels, e.g. "Low, Expired".
 * @param {Array<string>} statuses Condition keys.
 * @returns {string}
 */
function joinStatusLabels(statuses) {
    return statuses.map((conditionStatus) => CONDITION_LABELS[conditionStatus]).join(", ");
}

/**
 * Check History - every completed check of each box (first aider and manager), newest first: who
 * checked it, what happened to the tag, the box condition found, and (expand a row) the full stock
 * snapshot. Defaults to the last 6 months.
 */
function CheckHistory() {
    const [tableState, setTableState] = useState({ page: 1, perPage: 10, sortKey: "completed_at", sortOrder: "desc" });
    const [search, setSearch] = useState("");
    const [campusFilter, setCampusFilter] = useState(EMPTY_FILTER);
    const [boxFilter, setBoxFilter] = useState(EMPTY_SEARCH_FILTER);
    const [checkTypeFilter, setCheckTypeFilter] = useState(EMPTY_FILTER);
    const [dateRange, setDateRange] = useState({ startDate: moment().subtract(6, "months").toDate(), endDate: new Date() });
    const { data: reportFilters } = useFirstAidReportFilters();

    const reportParams = useMemo(() => ({
        page: tableState.page,
        limit: tableState.perPage,
        key: tableState.sortKey,
        sort: tableState.sortOrder,
        search,
        viaCampus: campusFilter.arr,
        viaBox: boxFilter.arr,
        viaCheckType: checkTypeFilter.arr,
        startDate: dateRange?.startDate ? moment(dateRange.startDate).format("YYYY-MM-DD") : undefined,
        endDate: dateRange?.endDate ? moment(dateRange.endDate).format("YYYY-MM-DD") : undefined,
    }), [tableState, search, campusFilter, boxFilter, checkTypeFilter, dateRange]);
    const { data: checkPage, isFetching } = useFirstAidCheckHistory(reportParams);

    // A new filter or search starts from page 1, or the table can land on a page that no longer exists.
    useEffect(() => {
        setTableState((currentState) => ({ ...currentState, page: 1 }));
    }, [search, campusFilter, boxFilter, checkTypeFilter, dateRange]);

    const columns = useMemo(
        () => [
            {
                name: "Date",
                selector: "completed_at",
                sortField: "completed_at",
                sortable: true,
                minWidth: "150px",
                cell: (row) => moment(row.completed_at).format(REPORT_DATE_TIME_FORMAT),
            },
            {
                name: "Type",
                selector: "event_type",
                sortField: "event_type",
                sortable: true,
                minWidth: "130px",
                cell: (row) => EVENT_TYPE_LABELS[row.event_type],
            },
            {
                name: "Checked By",
                selector: "opened_by_name",
                sortField: "opened_by_name",
                sortable: true,
                minWidth: "190px",
                cell: (row) => <OhsUserCell user={getPrefixedUser(row, "opened_by", row.opened_by_id)} />,
            },
            {
                name: "Box",
                selector: "box_code",
                sortField: "box_code",
                sortable: true,
                minWidth: "190px",
                cell: (row) => (
                    <div>
                        <Link to={`${OHS_BASE_PATH}/boxes/open/${row.box_id}`}>{row.box_code}</Link>
                        <div><MutedText>{row.campus_name} · {row.building_name} · {row.area_location}</MutedText></div>
                    </div>
                ),
            },
            {
                name: "Tag",
                minWidth: "220px",
                cell: (row) => {
                    const tagCheck = describeTagCheck(row);
                    return (
                        <div>
                            <div>{describeTagChange(row)}</div>
                            {tagCheck.isFlagged && <ConditionBadge $condition="damaged" title={tagCheck.label}>Flagged</ConditionBadge>}
                        </div>
                    );
                },
            },
            {
                name: "Box Condition",
                minWidth: "170px",
                cell: (row) => <ConditionBadges statuses={row.statuses} />,
            },
            {
                name: "Needs Attention",
                minWidth: "220px",
                grow: 2,
                cell: (row) => {
                    const problemItems = row.items.filter((snapshotItem) => getItemStatuses(snapshotItem)[0] !== "ok");
                    if (!problemItems.length) {
                        return <MutedText>Nothing - all items OK</MutedText>;
                    }
                    return (
                        <div>
                            {problemItems.map((snapshotItem) => (
                                <div key={snapshotItem.box_item_id}>
                                    {snapshotItem.item_name} <MutedText>({joinStatusLabels(getItemStatuses(snapshotItem))}, {snapshotItem.quantity} of {snapshotItem.expected_quantity})</MutedText>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
        ],
        []
    );

    /**
     * Exports every check matching the current filters (not just this page). One row per check, then a
     * Condition / Quantity / Expiry column group per item, in catalog order.
     * @param {string} fileType xlsx | csv | pdf
     * @param {string} fileName Download name.
     */
    const handleExport = async (fileType, fileName) => {
        try {
            const { data } = await GetFirstAidCheckHistory({ ...reportParams, exportStatus: "true" });
            const checkRows = data?.data ?? [];

            // Every item seen on any exported check - a catalog change between checks still gets its own columns.
            const exportItemsById = new Map();
            checkRows.forEach((check) => check.items.forEach((snapshotItem) => exportItemsById.set(snapshotItem.box_item_id, snapshotItem)));
            const exportItems = [...exportItemsById.values()].sort((first, second) => first.display_order - second.display_order);

            await exportOhsReport({
                fileType,
                fileName,
                frozenColumns: 8,
                columns: [
                    ...CHECK_EXPORT_COLUMNS,
                    ...exportItems.flatMap((exportItem) => {
                        const itemGroup = `${exportItem.item_name} (holds ${exportItem.expected_quantity} ${exportItem.unit})`;
                        return [
                            { group: itemGroup, label: "Condition", width: 14 },
                            { group: itemGroup, label: "Quantity", width: 9 },
                            { group: itemGroup, label: "Expiry", width: 11 },
                        ];
                    }),
                ],
                rows: checkRows.map((check) => {
                    const snapshotById = new Map(check.items.map((snapshotItem) => [snapshotItem.box_item_id, snapshotItem]));
                    const problemItems = check.items.filter((snapshotItem) => getItemStatuses(snapshotItem)[0] !== "ok");
                    return [
                        moment(check.completed_at).format("YYYY-MM-DD"),
                        moment(check.completed_at).format("HH:mm"),
                        EVENT_TYPE_LABELS[check.event_type],
                        check.opened_by_name,
                        check.campus_name,
                        check.building_name,
                        check.area_location,
                        check.box_code,
                        check.tag_serial_found || "No tag",
                        check.is_tag_intact ? "Not replaced - box not opened" : check.new_tag_serial,
                        { value: describeTagCheck(check).label, tone: describeTagCheck(check).isFlagged ? "flagged" : "ok" },
                        { value: joinStatusLabels(check.statuses), tone: check.statuses[0] },
                        { value: problemItems.length, tone: problemItems.length ? "low" : "ok" },
                        ...exportItems.flatMap((exportItem) => {
                            const snapshotItem = snapshotById.get(exportItem.box_item_id);
                            // An item that wasn't in the catalog when this check was done.
                            if (!snapshotItem) {
                                return ["", "", ""];
                            }
                            const itemStatuses = getItemStatuses(snapshotItem);
                            return [
                                // Only problems are coloured - green on every OK item would drown them out.
                                { value: joinStatusLabels(itemStatuses), tone: itemStatuses[0] === "ok" ? null : itemStatuses[0] },
                                snapshotItem.quantity,
                                snapshotItem.expiry_date || "",
                            ];
                        }),
                    ];
                }),
                pdfSections: checkRows.map((check) => ({
                    title: `${moment(check.completed_at).format(REPORT_DATE_TIME_FORMAT)} - ${EVENT_TYPE_LABELS[check.event_type]} of ${check.box_code} (${check.campus_name}, ${check.building_name}, ${check.area_location})`,
                    summary: `Checked by ${check.opened_by_name} · Tag: ${describeTagChange(check)} · ${describeTagCheck(check).label} · Box condition: ${joinStatusLabels(check.statuses)}`,
                    header: ["Item", "Quantity", "Expected", "Expiry Date", "Status"],
                    rows: check.items.map((snapshotItem) => [
                        snapshotItem.item_name,
                        snapshotItem.quantity,
                        snapshotItem.expected_quantity,
                        snapshotItem.expiry_date ? moment(snapshotItem.expiry_date).format("D MMM YYYY") : "-",
                        joinStatusLabels(getItemStatuses(snapshotItem)),
                    ]),
                })),
            });
        } catch (error) {
            console.log("CheckHistory.js->handleExport->Error", error);
            Swal.fire("Error", "The check history could not be exported.", "error");
        }
    };

    return (
        <DataTableComponent
            data={checkPage?.data ?? []}
            loading={isFetching}
            state={tableState}
            setState={setTableState}
            setSearch={setSearch}
            totalRows={checkPage?.total ?? 0}
            columns={columns}
            exportFunction={handleExport}
            exportFileName="First_Aid_Check_History"
            filters={[
                {
                    filterName: "Date",
                    state: dateRange,
                    setState: setDateRange,
                    dateRangeFilter: true,
                },
                {
                    filterName: "Campus",
                    optionArr: reportFilters.CampusFilter || [],
                    state: campusFilter,
                    setState: setCampusFilter,
                    uniqueId: "ohsHistoryCampus",
                },
                {
                    filterName: "Box",
                    optionArr: reportFilters.BoxFilter || [],
                    state: boxFilter,
                    setState: setBoxFilter,
                    uniqueId: "ohsHistoryBox",
                    isSearchFilter: true,
                },
                {
                    filterName: "Check Type",
                    optionArr: reportFilters.CheckTypeFilter || [],
                    state: checkTypeFilter,
                    setState: setCheckTypeFilter,
                    uniqueId: "ohsHistoryType",
                },
            ]}
        />
    );
}

export default CheckHistory;
